import { useMemo, useState } from "react";
import { MessageCircle } from "lucide-react";
import type { InSnipCard } from "../types";
import { deriveFollowUps } from "./followups";

/**
 * "Ask the story": grounded follow-up chips under the card body. Tapping a chip expands its
 * pre-grounded answer instantly (no AI call, works anonymously); the trailing chip hands off
 * to the real Ask AI when the host app provides it. Mount with key={card.id} so state resets
 * per card. House style: no em dashes or en dashes anywhere in copy.
 */
export function FollowUpChips({
  card,
  onAskMore,
  onOpen,
}: {
  card: InSnipCard;
  /** Present only when the live Ask AI is available; renders the handoff chip. */
  onAskMore?: (() => void) | null;
  /** Analytics hook, called with the opened question. */
  onOpen?: (question: string) => void;
}) {
  const chips = useMemo(() => deriveFollowUps(card, 3), [card]);
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  if (!chips.length) return null;

  return (
    <div className="fu-wrap">
      <span className="fu-label">Ask the story</span>
      <div className="fu-chips" role="group" aria-label="Follow-up questions">
        {chips.map((c, i) => (
          <button
            key={c.q}
            type="button"
            className={i === openIdx ? "fu-chip open" : "fu-chip"}
            aria-expanded={i === openIdx}
            onClick={() => {
              const next = openIdx === i ? null : i;
              setOpenIdx(next);
              if (next != null) onOpen?.(c.q);
            }}
          >
            {c.q}
          </button>
        ))}
        {onAskMore ? (
          <button type="button" className="fu-chip fu-more" onClick={onAskMore}>
            <MessageCircle size={13} aria-hidden="true" /> Ask anything
          </button>
        ) : null}
      </div>
      {openIdx != null && chips[openIdx] ? (
        <div className="fu-answer">
          <p>{chips[openIdx].a}</p>
          <small>From this story's own reporting</small>
        </div>
      ) : null}
    </div>
  );
}
